<template>
  <!-- Вложения сообщения: картинки и видео открываются в просмотрщике,
       аудио играет прямо в пузыре, остальное — карточкой со скачиванием -->
  <div v-if="files?.length" class="flex flex-col gap-2">
    <button
      v-for="(file, index) in media"
      :key="`media-${index}`"
      type="button"
      class="relative block w-full max-w-[260px] cursor-zoom-in overflow-hidden rounded-xl bg-black/5 text-left"
      :title="chatFileName(file)"
      @click.stop="viewerIndex = index"
    >
      <img
        v-if="getChatFileKind(file) === 'image'"
        :src="file.url"
        :alt="chatFileName(file)"
        loading="lazy"
        class="w-full object-cover"
      />
      <template v-else>
        <!-- Без фрагмента Safari не рисует первый кадр -->
        <video
          :src="`${file.url}#t=0.001`"
          preload="metadata"
          muted
          playsinline
          class="aspect-video w-full bg-black object-cover"
        />
        <span
          class="absolute left-1/2 top-1/2 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 !text-white"
        >
          <Icon name="lucide:play" size="22" class="block translate-x-px !text-white" />
        </span>
      </template>
    </button>

    <audio
      v-for="(file, index) in audio"
      :key="`audio-${index}`"
      :src="file.url"
      controls
      preload="metadata"
      class="h-10 w-full max-w-[260px]"
      :title="chatFileName(file)"
      @click.stop
    />

    <button
      v-for="(file, index) in documents"
      :key="`doc-${index}`"
      type="button"
      class="flex w-full max-w-[260px] items-center gap-2 rounded-xl px-3 py-2 text-left transition-opacity hover:opacity-80"
      :class="dark ? 'bg-white/20' : 'bg-black/5'"
      @click.stop="downloadFile(file.url, file.name || undefined)"
    >
      <Icon name="lucide:paperclip" size="16" class="shrink-0" />
      <span class="min-w-0 flex-1 truncate text-sm">
        {{ file.name || "Файл" }}
      </span>
      <span v-if="chatFileSize(file.size)" class="shrink-0 text-xs opacity-70">
        {{ chatFileSize(file.size) }}
      </span>
    </button>

    <ChatAttachmentViewer
      v-if="viewerIndex !== null"
      :files="media"
      :start-index="viewerIndex"
      @close="viewerIndex = null"
    />
  </div>
</template>

<script setup lang="ts">
  import type { IChatFile } from "~/interfaces/chat";
  import { chatFileName, chatFileSize, getChatFileKind } from "~/common/chatFiles";

  const props = defineProps<{
    files?: IChatFile[];
    // Пузырь с цветным фоном — карточку файла делаем светлой
    dark?: boolean;
  }>();

  const { downloadFile } = useFileDownload();

  const byKind = (...kinds: string[]) =>
    (props.files ?? []).filter((file) => kinds.includes(getChatFileKind(file)));

  const media = computed(() => byKind("image", "video"));
  const audio = computed(() => byKind("audio"));
  const documents = computed(() => byKind("file"));

  const viewerIndex = ref<number | null>(null);
</script>
